/*
 * Copyright Evollis 2021
 * All Rights Reserved.
 *
 * All information contained herein is, and remains the property of Evollis.
 * The intellectual and technical concepts contained herein are
 * proprietary to Evollis and are protected by trade secret and copyright law.
 * Dissemination of this information or reproduction of this material
 * is STRICTLY FORBIDDEN unless prior written permission is obtained
 * from Evollis.
 */
import InformationButton from "../../../components/molecules/InformationButton"
import SelectField from "../../../components/molecules/SelectField/SelectField"
import SmallBusinessToolTipDescription from "../../../components/molecules/SmallBusinessToolTipDescription"
import SelectOrTextInput from "../../../components/organisms/SelectOrTextInput/SelectOrTextInput"
import { TENANTS } from "../../../constants"
import { getCountryFromLanguage } from "../../../core/service/BusinessLogicService"

/* Utils */
import { loc } from "../../../core/utils"
import { getSessionVoyageurLang } from "../../../core/voyageurLanguage"
import CheckboxField from "../../../form/CheckboxField"
import Date from "../../../form/Date"
import RadiosField from "../../../form/RadiosField"
import Select from "../../../form/Select"

/* Forms controls & validation */
import TextField from "../../../form/TextField"
import SelectIdentificationClientPage from "../../IdentificationPage/components/componentsByCountry/ITComponents/SelectIdentificationClientPage"
import { DATE_FORMAT, italian_order_language_tag, spanish_order_language_tag } from "../constants/ClientPageConstants"
import { getCivilStatuses } from "../service/ClientPageServices"
import Bloc from "./Bloc"
import useIdentityBloc from "./useIdentityBloc"
import { isNIE } from "better-dni"
import React from "react"
import { Col, Row } from "react-bootstrap"

export default function IdentityBloc(props) {
  const { tunnelData, values, country, blocFields, completedFields } = props
  const { clientPageConfiguration } = tunnelData
  let { identity, leaderIdentity, company } = clientPageConfiguration

  identity = identity ? identity : leaderIdentity
  const {
    countries,
    isLoadingCountries,
    birthDepartmentOptions,
    isLoadingBirthDepartmentOptions,
    cities,
    isLoadingCities
  } = useIdentityBloc({ tunnelData, values })

  const isSamsungIT = tunnelData?.tenant === TENANTS.SAMSUNG_IT
  const isSamsungESApz = tunnelData?.tenant === TENANTS.SAMSUNG_ES_APZ
  const isMediaMarkt = tunnelData?.tenant === TENANTS.MEDIAMARKT_NL || tunnelData?.tenant === TENANTS.MEDIAMARKT_BE
  const selectedLanguageTag = getSessionVoyageurLang() || tunnelData?.data?.order?.orderLanguageTag || ""
  const useMediaMarktDutchLabels = isMediaMarkt && selectedLanguageTag.startsWith("nl")
  const isFirstNameBeforeLastName =
    identity?.includes("firstName") &&
    identity?.includes("name") &&
    identity.indexOf("firstName") < identity.indexOf("name")

  const firstNameField = (
    <Col lg={12}>
      <TextField
        hidden={!identity?.includes("firstName") && !identity?.includes("leaderFirstName")}
        name="identity.firstName"
        label={identity?.includes("firstName") ? loc`First name` : loc`leader first name`}
        trimOnBlur={true}
        disabled={
          isSamsungIT ||
          (tunnelData?.data?.order.simplifiedJourneyEnabled &&
            !tunnelData?.simplifiedJourneyInputConfiguration?.identity?.firstName?.editable)
        }
      />
    </Col>
  )

  return (
    <Bloc
      hide={!identity || company}
      name={tunnelData.changeTradForLeaderBpls ? "My Leader identity" : "My identity"}
      completedFields={completedFields}
      totalFieldsNumber={blocFields.length}
    >
      <div className="hard-shadow-white-block">
        <Row>
          {identity?.includes("isSmallBusiness") && (
            <Col lg={12}>
              <CheckboxField
                hidden={!identity?.includes("isSmallBusiness")}
                name="identity.isSmallBusiness"
                checked={values.identity?.isSmallBusiness}
                label={loc`SmallBusiness`}
                disabled={
                  !!tunnelData?.data?.order?.simplifiedJourneyEnabled &&
                  tunnelData?.simplifiedJourneyInputConfiguration?.identity?.isSmallBusiness != null &&
                  !tunnelData.simplifiedJourneyInputConfiguration.identity.isSmallBusiness.editable
                }
              >
                <InformationButton
                  overlayText={loc("SmallBusinessDescription", { text: <SmallBusinessToolTipDescription /> })}
                />
              </CheckboxField>
            </Col>
          )}
          <Col lg={12}>
            <RadiosField
              hidden={!identity?.includes("title")}
              name="identity.title"
              checked={values.identity.title}
              items={getCivilStatuses(tunnelData)}
              label={loc`Title`}
              disabled={
                isSamsungIT ||
                (tunnelData?.data?.order.simplifiedJourneyEnabled &&
                  !tunnelData?.simplifiedJourneyInputConfiguration?.identity?.title?.editable)
              }
            />
          </Col>
          {isFirstNameBeforeLastName && firstNameField}
          <Col lg={12}>
            <TextField
              hidden={!identity?.includes("name") && !identity?.includes("leaderName")}
              name="identity.name"
              label={
                identity?.includes("name")
                  ? loc(useMediaMarktDutchLabels ? "Family name" : "Last name")
                  : loc`leader name`
              }
              trimOnBlur={true}
              disabled={
                isSamsungIT ||
                (tunnelData?.data?.order.simplifiedJourneyEnabled &&
                  !tunnelData?.simplifiedJourneyInputConfiguration?.identity?.name?.editable)
              }
            />
          </Col>
          {!isFirstNameBeforeLastName && firstNameField}
          <Col lg={7}>
            <TextField
              hidden={!identity?.includes("taxNumber")}
              name="identity.taxNumber"
              label={loc`Tax number`}
              trimOnBlur={true}
              disabled={
                isSamsungIT ||
                (tunnelData?.data?.order.simplifiedJourneyEnabled &&
                  !tunnelData?.simplifiedJourneyInputConfiguration?.identity?.taxNumber?.editable)
              }
              maj
            />
          </Col>
          {identity?.includes("identificationExpirationDate") && !isSamsungESApz && (
            <Col lg={5}>
              <Date
                hidden={!identity?.includes("identificationExpirationDate")}
                name="identity.identificationExpirationDate"
                label={loc`Identification expiration date`}
                placeholder={DATE_FORMAT.identity.identificationExpirationDate[country]}
                format={DATE_FORMAT.identity.identificationExpirationDate[country]}
                disabled={tunnelData?.data?.order.orderLanguageTag !== spanish_order_language_tag}
              />
            </Col>
          )}
          {identity?.includes("typeOfResidence") && isNIE(values.identity.taxNumber) && (
            <Col lg={4}>
              <Select
                hidden={!isNIE(values.identity.taxNumber) || !identity?.includes("typeOfResidence")}
                name="identity.typeOfResidence"
                label={loc`Type of residence`}
                placeholder={loc`Select...`}
                url={
                  tunnelData.apiUrl +
                  "middle-business-service/api/v2/repository/type-residence/" +
                  getCountryFromLanguage(tunnelData?.data?.order.orderLanguageTag)
                }
                token={tunnelData.access_token}
              />
            </Col>
          )}
          {identity?.includes("citizenShipCountry") && (
            <Col lg={8}>
              <SelectField
                options={countries}
                name="identity.citizenShipCountry"
                label={loc`Citizenship`}
                placeholder={loc`Select...`}
                isLoading={isLoadingCountries}
                openMenuOnFocus={true}
                disabled={
                  tunnelData?.data?.order.simplifiedJourneyEnabled &&
                  !tunnelData?.simplifiedJourneyInputConfiguration?.identity?.citizenShipCountry?.editable
                }
              />
            </Col>
          )}
          <Col lg={4}>
            <Date
              hidden={!identity?.includes("birthDate")}
              name="identity.birthDate"
              label={loc`Birthdate`}
              format={DATE_FORMAT.identity.birthDate[country]}
              disabled={
                tunnelData?.data?.order.orderLanguageTag === italian_order_language_tag
                  ? values.identity.taxNumber ||
                    (tunnelData?.data?.order.simplifiedJourneyEnabled &&
                      !tunnelData?.simplifiedJourneyInputConfiguration?.identity?.birthDate.editable)
                  : tunnelData?.data?.order.simplifiedJourneyEnabled &&
                    !tunnelData?.simplifiedJourneyInputConfiguration?.identity?.birthDate.editable
              }
            />
          </Col>

          <Col lg={12}>
            <Select
              hidden={!identity?.includes("identityDocumentType") || isSamsungESApz}
              name="identity.identityDocumentType"
              label={loc`Identity document type`}
              placeholder={loc`Select...`}
              url={
                values.identity.citizenShipCountry?.value &&
                tunnelData.apiUrl +
                  "middle-business-service/api/v2/repository/document-type/" +
                  getCountryFromLanguage(tunnelData?.data?.order.orderLanguageTag) +
                  "?citizenShipCountry=" +
                  values.identity.citizenShipCountry.value
              }
              token={tunnelData.access_token}
              disabled={
                !values.identity.citizenShipCountry ||
                (tunnelData?.data?.order.simplifiedJourneyEnabled &&
                  !tunnelData?.simplifiedJourneyInputConfiguration?.identity?.identityDocumentType?.editable)
              }
            />
          </Col>
          {tunnelData.tenant === "samsung_it" ? (
            <Col lg={12}>
              <SelectIdentificationClientPage tunnelData={tunnelData} values={values} country={country} />
            </Col>
          ) : (
            <>
              <Col lg={12}>
                {identity?.includes("birthCountry") && (
                  <SelectField
                    options={countries}
                    name="identity.birthCountry"
                    label={loc("Country of birth")}
                    placeholder={loc("Select...")}
                    isLoading={isLoadingCountries}
                    openMenuOnFocus={true}
                    tenant={tunnelData.tenant}
                    financerType={tunnelData?.data?.order.financerType}
                    disabled={
                      tunnelData?.data?.order.orderLanguageTag === italian_order_language_tag
                        ? values.identity.taxNumber ||
                          (tunnelData?.data?.order.simplifiedJourneyEnabled &&
                            !tunnelData?.simplifiedJourneyInputConfiguration?.identity?.birthCountry.editable)
                        : tunnelData?.data?.order.simplifiedJourneyEnabled &&
                          !tunnelData?.simplifiedJourneyInputConfiguration?.identity?.birthCountry.editable
                    }
                  />
                )}
              </Col>

              <Col lg={12}>
                {identity?.includes("birthDepartment") && (
                  <SelectOrTextInput
                    options={birthDepartmentOptions}
                    name="identity.birthDepartment"
                    placeholder={loc`Select...`}
                    label={loc`Department of birth`}
                    isLoading={isLoadingBirthDepartmentOptions}
                    disabled={
                      tunnelData?.data?.order.orderLanguageTag === italian_order_language_tag
                        ? values.identity.taxNumber ||
                          !values.identity.birthCountry ||
                          (tunnelData?.data?.order.simplifiedJourneyEnabled &&
                            !tunnelData?.simplifiedJourneyInputConfiguration?.identity?.birthDepartment.editable)
                        : tunnelData?.data?.order.simplifiedJourneyEnabled &&
                          !tunnelData?.simplifiedJourneyInputConfiguration?.identity?.birthDepartment.editable
                    }
                    isSelect={isArrayFilledWithData(birthDepartmentOptions) || isLoadingBirthDepartmentOptions}
                  />
                )}
              </Col>
              <Col lg={12}>
                {identity?.includes("birthCity") && (
                  <SelectOrTextInput
                    options={cities}
                    name="identity.birthCity"
                    placeholder={loc`Select...`}
                    label={loc`City of birth`}
                    isLoading={isLoadingCities}
                    disabled={
                      tunnelData?.data?.order.orderLanguageTag === italian_order_language_tag
                        ? values.identity.taxNumber ||
                          !values.identity.birthDepartment ||
                          (tunnelData?.data?.order.simplifiedJourneyEnabled &&
                            !tunnelData?.simplifiedJourneyInputConfiguration?.identity?.birthCity.editable)
                        : tunnelData?.data?.order.simplifiedJourneyEnabled &&
                          !tunnelData?.simplifiedJourneyInputConfiguration?.identity?.birthCity.editable
                    }
                    isSelect={isArrayFilledWithData(cities) || isLoadingCities}
                  />
                )}
              </Col>
            </>
          )}
        </Row>
      </div>
    </Bloc>
  )

  function isArrayFilledWithData(element) {
    return element !== null && element?.length > 0
  }
}
